import { Switch, Route } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AppErrorBoundary, PageErrorBoundary } from "@/components/ErrorBoundary";
import { useNavigationTracking } from "@/hooks/use-sentry";
import { usePageTracking, useEngagementTracking } from "@/hooks/use-analytics";
import { CookieConsentProvider } from "@/components/CookieConsentProvider";
import { MobileNavigation } from "@/components/mobile-navigation";
import Home from "@/pages/home";
import NotFound from "@/pages/not-found";
import Checkout from "@/pages/checkout";
import CheckoutSuccess from "@/pages/checkout-success";
import Waitlist from "@/pages/waitlist";
import WaitlistSuccess from "@/pages/waitlist-success";
import Portal from "@/pages/portal";
import Admin from "@/pages/admin";
import Workshop from "@/pages/workshop";
import WorkshopSuccess from "@/pages/workshop-success";
import Privacy from "@/pages/privacy";
import Terms from "@/pages/terms";
// Cookie consent disabled - not required outside EU
// import Cookies from "@/pages/cookies";
import Gallery from "@/pages/gallery";
import CommunityGuidelines from "@/pages/community-guidelines";
import EmailPreferences from "@/pages/email-preferences";

function NavigationTracker() {
  useNavigationTracking();
  return null;
}

/**
 * GA4 Analytics Tracker
 * Tracks page views and engagement metrics for Google Analytics 4
 */
function AnalyticsTracker() {
  usePageTracking();
  useEngagementTracking();
  return null;
}

function Router() {
  return (
    <Switch>
      <Route path="/">
        <PageErrorBoundary>
          <Home />
        </PageErrorBoundary>
      </Route>
      <Route path="/checkout">
        <PageErrorBoundary>
          <Checkout />
        </PageErrorBoundary>
      </Route>
      <Route path="/checkout/success">
        <PageErrorBoundary>
          <CheckoutSuccess />
        </PageErrorBoundary>
      </Route>
      <Route path="/waitlist">
        <PageErrorBoundary>
          <Waitlist />
        </PageErrorBoundary>
      </Route>
      <Route path="/waitlist/success">
        <PageErrorBoundary>
          <WaitlistSuccess />
        </PageErrorBoundary>
      </Route>
      <Route path="/portal">
        <PageErrorBoundary>
          <Portal />
        </PageErrorBoundary>
      </Route>
      <Route path="/portal/:token">
        <PageErrorBoundary>
          <Portal />
        </PageErrorBoundary>
      </Route>
      <Route path="/admin">
        <PageErrorBoundary>
          <Admin />
        </PageErrorBoundary>
      </Route>
      <Route path="/workshop">
        <PageErrorBoundary>
          <Workshop />
        </PageErrorBoundary>
      </Route>
      <Route path="/workshop/success">
        <PageErrorBoundary>
          <WorkshopSuccess />
        </PageErrorBoundary>
      </Route>
      <Route path="/privacy">
        <PageErrorBoundary>
          <Privacy />
        </PageErrorBoundary>
      </Route>
      <Route path="/terms">
        <PageErrorBoundary>
          <Terms />
        </PageErrorBoundary>
      </Route>
      {/* Cookie consent disabled - not required outside EU
      <Route path="/cookies">
        <PageErrorBoundary>
          <Cookies />
        </PageErrorBoundary>
      </Route>
      */}
      <Route path="/gallery">
        <PageErrorBoundary>
          <Gallery />
        </PageErrorBoundary>
      </Route>
      <Route path="/community-guidelines">
        <PageErrorBoundary>
          <CommunityGuidelines />
        </PageErrorBoundary>
      </Route>
      <Route path="/email-preferences">
        <PageErrorBoundary>
          <EmailPreferences />
        </PageErrorBoundary>
      </Route>
      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  return (
    <AppErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <CookieConsentProvider>
            <NavigationTracker />
            <AnalyticsTracker />
            <Toaster />
            <MobileNavigation
              enableSwipeBack={true}
              enableTransitions={true}
              transitionMode="slide-up"
            >
              <Router />
            </MobileNavigation>
          </CookieConsentProvider>
        </TooltipProvider>
      </QueryClientProvider>
    </AppErrorBoundary>
  );
}

export default App;
